/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback } from 'react';
import { Linking } from 'react-native';
import { openInAppBrowser, type openModuleProps } from '@airasia-rn/ui';
import { useAccountContext } from '../context/AccountContext';

export type OpenUrl = (url?: string) => void;

/**
 * Returns a function that opens an external URL. When the super app host
 * provides `openModule` (via AccountContext) the URL is handed to it as a
 * deeplink; otherwise it falls back to the previous behaviour — open in the
 * in-app browser, then the OS `Linking` handler.
 */
export const useOpenUrl = (): OpenUrl => {
  const { openModule } = useAccountContext();

  return useCallback(
    (url?: string) => {
      const target = url?.trim();
      if (!target) return;
      if (openModule) {
        openModule({ deeplink: target } as openModuleProps);
        return;
      }
      openInAppBrowser(target).catch(() => {
        Linking.openURL(target).catch(() => {
          console.error('Failed to open url');
        });
      });
    },
    [openModule],
  );
};
